// MasterDetail — patrón global (§6.1): lista + detalle 50/50 (`.md-shell` del mock), cada lado
// con scroll propio. Lo usan políticas, promociones, segmentos, mensajes, MDM y conciliación.
//
// Dos variantes del mismo patrón:
//   · `MasterDetail`         — 2 columnas al ras, el default.
//   · `MasterDetailExplorer` — 3 columnas (filtros · lista · detalle), `.explorer` del mock.
import { useCallback, useRef, useState, type PointerEvent, type ReactNode } from 'react';
import { cn } from '@/lib/cn';

export function MasterDetail({
    list,
    detail,
    className,
}: {
    list: ReactNode;
    detail: ReactNode;
    className?: string;
}) {
    return (
        <div
            className={cn(
                'grid min-h-0 flex-1 grid-cols-2 overflow-hidden bg-background',
                className,
            )}
        >
            <div className="flex min-h-0 flex-col border-r border-border bg-card">{list}</div>
            <div className="flex min-h-0 flex-col bg-muted/15">{detail}</div>
        </div>
    );
}

/**
 * Variante EXPLORER (`.explorer` del mock, patrón v3 canónico de SureKeep): tres columnas —
 * filtros · lista · detalle — en `200px 3fr 2fr`, cada una con su propio scroll.
 *
 * Va A SANGRE, como el `.md-shell` del mock: sin gap ni esquinas redondeadas, las columnas
 * separadas solo por la línea de la derecha. El mock de Cartera lo nombra «patrón plano unificado
 * con Clientes/Excepciones», así que es del patrón y no de cada pantalla.
 *
 * Es una VARIANTE y no un componente aparte porque sigue siendo lista+detalle, solo que con una
 * columna de filtros pegada a la izquierda. Para pantallas sin filtros laterales, `MasterDetail`
 * (2 columnas al ras) sigue siendo el default.
 *
 * Colapsada, la columna de filtros baja a un riel de 40px; qué se renderiza adentro en ese estado
 * lo decide el consumidor.
 */
export function MasterDetailExplorer({
    filters,
    list,
    detail,
    filtersCollapsed = false,
    storageKey,
    listMinWidth,
    split,
    className,
}: {
    filters: ReactNode;
    list: ReactNode;
    detail: ReactNode;
    filtersCollapsed?: boolean;
    /**
     * Con qué nombre recordar el ancho del detalle en este navegador. Sin él la columna se
     * redimensiona igual, pero vuelve al default al recargar.
     */
    storageKey?: string;
    /** Ancho mínimo de la lista en px; en pantallas angostas cede para que filtros y detalle quepan. */
    listMinWidth?: number;
    /**
     * Reparto de lista y detalle como `grid-template-columns`; sin él, el 3fr/2fr de siempre.
     * Con `split` se ignora `listMinWidth`: los mínimos van dentro de las pistas.
     */
    split?: ExplorerSplit;
    className?: string;
}) {
    const shellRef = useRef<HTMLDivElement>(null);
    const grab = useDetailWidth(shellRef, storageKey);
    const filtersWidth = filtersCollapsed ? FILTERS_RAIL_PX : FILTERS_PX;

    return (
        <div
            ref={shellRef}
            className={cn(
                'grid min-h-0 flex-1 overflow-hidden bg-background',
                // Durante el arrastre la transición va contra el cursor: la columna llega tarde.
                grab.dragging ? '' : 'transition-[grid-template-columns]',
                className,
            )}
            style={{
                gridTemplateColumns:
                    grab.width === null
                        ? explorerGridColumns(filtersCollapsed, listMinWidth, split)
                        : `${String(filtersWidth)}px minmax(0,1fr) ${String(grab.width)}px`,
            }}
        >
            <ExplorerPanel className="border-r border-border">{filters}</ExplorerPanel>
            <ExplorerPanel className="border-r border-border">{list}</ExplorerPanel>
            <ExplorerPanel className="relative bg-muted/15">
                <ColumnGrab grab={grab} />
                {detail}
            </ExplorerPanel>
        </div>
    );
}

const FILTERS_PX = 200;
const FILTERS_RAIL_PX = 40;
/** Ninguna de las dos columnas puede quedar en nada: por debajo de esto la tabla deja de leerse. */
const MIN_DETAIL_PX = 300;

/** Pistas de la lista y del detalle, tal cual van al `grid-template-columns`. */
export interface ExplorerSplit {
    list: string;
    detail: string;
}

/** Columnas del explorer sin arrastre. El mínimo de la lista cede para que el detalle nunca se salga. */
export function explorerGridColumns(
    filtersCollapsed: boolean,
    listMinWidth?: number,
    split?: ExplorerSplit,
): string {
    const filtersWidth = filtersCollapsed ? FILTERS_RAIL_PX : FILTERS_PX;
    if (split !== undefined) return `${String(filtersWidth)}px ${split.list} ${split.detail}`;
    if (listMinWidth === undefined) return `${String(filtersWidth)}px minmax(0,3fr) minmax(0,2fr)`;
    const listMin = `min(${String(listMinWidth)}px, calc(100% - ${String(filtersWidth + MIN_DETAIL_PX)}px))`;
    return `${String(filtersWidth)}px minmax(${listMin},3fr) minmax(${String(MIN_DETAIL_PX)}px,2fr)`;
}
const MIN_LIST_PX = 320;
/** Cuánto mueve cada flecha del teclado. */
const KEY_STEP_PX = 16;

function clampDetailWidth(candidate: number, shell: HTMLElement): number {
    const room = shell.getBoundingClientRect().width - MIN_LIST_PX;
    const max = Math.max(MIN_DETAIL_PX, room);
    return Math.min(Math.max(candidate, MIN_DETAIL_PX), max);
}

/** El ancho recordado. El acceso a `localStorage` truena en algunos contextos: nunca sin guarda. */
function readStoredWidth(storageKey: string | undefined): number | null {
    if (storageKey === undefined) return null;
    try {
        // eslint-disable-next-line no-restricted-syntax -- ancho del panel, sin tokens ni PII
        const raw = window.localStorage.getItem(`explorer-detail-width:${storageKey}`);
        const parsed = raw === null ? Number.NaN : Number(raw);
        return Number.isFinite(parsed) ? parsed : null;
    } catch {
        return null;
    }
}

function writeStoredWidth(storageKey: string | undefined, width: number | null): void {
    if (storageKey === undefined) return;
    const key = `explorer-detail-width:${storageKey}`;
    try {
        // eslint-disable-next-line no-restricted-syntax -- ancho del panel, sin tokens ni PII
        if (width === null) window.localStorage.removeItem(key);
        // eslint-disable-next-line no-restricted-syntax -- ancho del panel, sin tokens ni PII
        else window.localStorage.setItem(key, String(Math.round(width)));
    } catch {
        // Un navegador que no deja guardar no es motivo para dejar de redimensionar.
    }
}

/** El ancho del detalle: arrastre, teclado y lo que este navegador recuerde. */
function useDetailWidth(shellRef: React.RefObject<HTMLDivElement | null>, storageKey?: string) {
    const [width, setWidth] = useState<number | null>(() => readStoredWidth(storageKey));
    const [dragging, setDragging] = useState(false);

    const applyWidth = useCallback(
        (candidate: number) => {
            const shell = shellRef.current;
            if (!shell) return;
            setWidth(clampDetailWidth(candidate, shell));
        },
        [shellRef],
    );

    const onGrabStart = useCallback((event: PointerEvent<HTMLDivElement>) => {
        event.preventDefault();
        event.currentTarget.setPointerCapture(event.pointerId);
        setDragging(true);
    }, []);

    const onGrabMove = useCallback(
        (event: PointerEvent<HTMLDivElement>) => {
            const shell = shellRef.current;
            if (!shell) return;
            applyWidth(shell.getBoundingClientRect().right - event.clientX);
        },
        [applyWidth, shellRef],
    );

    const onGrabEnd = useCallback(
        (event: PointerEvent<HTMLDivElement>) => {
            event.currentTarget.releasePointerCapture(event.pointerId);
            setDragging(false);
            setWidth((current) => {
                writeStoredWidth(storageKey, current);
                return current;
            });
        },
        [storageKey],
    );

    const onKeyDown = useCallback(
        (event: React.KeyboardEvent<HTMLDivElement>) => {
            if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
            const shell = shellRef.current;
            if (!shell) return;
            event.preventDefault();
            const current = width ?? shell.getBoundingClientRect().width * 0.4;
            const next = clampDetailWidth(
                current + (event.key === 'ArrowLeft' ? KEY_STEP_PX : -KEY_STEP_PX),
                shell,
            );
            setWidth(next);
            writeStoredWidth(storageKey, next);
        },
        [shellRef, storageKey, width],
    );

    const reset = useCallback(() => {
        setWidth(null);
        writeStoredWidth(storageKey, null);
    }, [storageKey]);

    return { width, dragging, onGrabStart, onGrabMove, onGrabEnd, onKeyDown, reset };
}

/**
 * El borde por el que se agarra la columna de detalle para hacerla más ancha o más angosta.
 *
 * Vive DENTRO del panel, pegado a su borde: el shell recorta lo que se le salga, así que un
 * separador montado sobre la línea quedaría cortado a la mitad. Doble clic devuelve el default.
 */
function ColumnGrab({ grab }: { grab: ReturnType<typeof useDetailWidth> }) {
    /* Un separador REDIMENSIONABLE es focusable por diseño (el window splitter de la APG); la regla
       de a11y solo conoce el separador decorativo, que sí es no-interactivo. */
    /* eslint-disable jsx-a11y/no-noninteractive-element-interactions, jsx-a11y/no-noninteractive-tabindex */
    return (
        <div
            role="separator"
            aria-orientation="vertical"
            aria-label="Ancho del panel de detalle"
            aria-valuemin={MIN_DETAIL_PX}
            aria-valuenow={grab.width ?? undefined}
            tabIndex={0}
            onPointerDown={grab.onGrabStart}
            onPointerMove={grab.dragging ? grab.onGrabMove : undefined}
            onPointerUp={grab.onGrabEnd}
            onDoubleClick={grab.reset}
            onKeyDown={grab.onKeyDown}
            className={cn(
                'absolute inset-y-0 left-0 z-20 w-1.5 cursor-col-resize transition-colors',
                'hover:bg-primary/40 focus-visible:bg-primary/40 focus-visible:outline-none',
                grab.dragging && 'bg-primary/60',
            )}
        />
    );
    /* eslint-enable jsx-a11y/no-noninteractive-element-interactions, jsx-a11y/no-noninteractive-tabindex */
}

/** Columna del explorer (`.md-filters` / `.md-list` / `.md-detail` del mock), sin marco propio. */
export function ExplorerPanel({
    children,
    className,
}: {
    children: ReactNode;
    className?: string;
}) {
    return (
        <section className={cn('flex min-h-0 min-w-0 flex-col overflow-hidden bg-card', className)}>
            {children}
        </section>
    );
}

/** Estado vacío del panel detalle (`.md-empty`): ícono + título + sub + acción opcional. */
export function MasterDetailEmpty({
    icon,
    title,
    sub,
    action,
}: {
    icon?: ReactNode;
    title: ReactNode;
    sub?: ReactNode;
    action?: ReactNode;
}) {
    return (
        <div className="flex flex-1 flex-col items-center justify-center px-5 py-10 text-center text-muted-foreground">
            {icon ? <div className="mb-3 text-muted-foreground/40">{icon}</div> : null}
            <div className="text-[13px] font-semibold text-foreground">{title}</div>
            {sub ? <div className="mt-1 text-[11.5px] leading-relaxed">{sub}</div> : null}
            {action ? <div className="mt-4">{action}</div> : null}
        </div>
    );
}
